import castArray from "lodash/fp/castArray";
import find from "lodash/fp/find";
import filter from "lodash/fp/filter";
import flatMap from "lodash/fp/flatMap";
import flow from "lodash/fp/flow";
import getOr from "lodash/fp/getOr";
import has from "lodash/has";
import head from "lodash/fp/head";
import includes from "lodash/fp/includes";
import isString from "lodash/fp/isString";
import isEmpty from "lodash/fp/isEmpty";
import isArray from "lodash/fp/isArray";
import isNil from "lodash/fp/isNil";
import map from "lodash/fp/map";
import negate from "lodash/fp/negate";
import size from "lodash/fp/size";
import groupBy from "lodash/fp/groupBy";
import mapValues from "lodash/fp/mapValues";
import reduce from "lodash/fp/reduce";
import get from "lodash/fp/get";
import pickBy from "lodash/fp/pickBy";
import keys from "lodash/fp/keys";

import selectUserChoices from "./selectUserChoices";
import selectUserChoiceValues from "./selectUserChoiceValues";
import selectUserGearEquipmentOptions from "./selectUserGearEquipmentOptions";
import { getLabel, shouldBeDisplayed } from "utils";

const isNotNil = negate(isNil);

function getValue(
  {
    label,
    value,
    selectedOption,
    checkedValue,
    checkedLabel,
    uncheckedValue,
    uncheckedLabel,
  },
  userChoiceValues
) {
  return flow(
    filter(({ v }) => isNotNil(v) && value === v),
    map(({ v, l }) => {
      const valueToUse = isNotNil(l)
        ? getLabel({ label: l, userChoiceValues })
        : v;

      return {
        value: valueToUse.length > 0 ? `(${valueToUse})` : valueToUse,
        image: selectedOption?.image,
      };
    }),
    (arr) => [
      ...arr,
      {
        label,
        value: selectedOption?.label || selectedOption?.value || value,
        image: selectedOption?.image,
        isIncluded: selectedOption?.isIncluded,
      },
    ],
    head
  )([
    { v: checkedValue, l: checkedLabel },
    { v: uncheckedValue, l: uncheckedLabel },
  ]);
}

function getValuesLength(userChoices) {
  return flow(
    filter((field) => has(userChoices, field.name)),
    size
  );
}

function getCustomisedBladeAdditionalFields(userChoices, t = (str) => str) {
  const hasBladeCustomisations = userChoices?.SkateCustomiseBlade?.value;

  return (data) =>
    [
      {
        ...data,
        fields: [
          ...data.fields,
          {
            name: "SkateCustomiseBlade",
          },
        ],
      },

      hasBladeCustomisations && {
        id: `${data.id}__customizations`,
        title: t("Blade Customizations"),
        viewSpecsOptions: {
          displayAs: "column",
        },
        fields: [
          {
            title: t("Personalization"),
            fields: [
              {
                name: "customizeBladePlayerName",
              },
              {
                name: "customizeBladePlayerNumber",
              },
              {
                name: "customizeBladeNoPersonalization",
              },
            ],
            viewSpecsOptions: {
              joinValuesWith: " ",
            },
          },
          {
            title: t("Profile"),
            fields: [
              {
                name: "customizeBladeProfile",
              },
              {
                name: "Performance Radius",
              },
            ],
            viewSpecsOptions: {
              joinValuesWith: " - ",
            },
          },
          {
            title: t("Sharpening"),
            fields: [
              {
                name: "customizeBladeSharpening",
              },
              {
                name: "Sharpening",
                ignoreImage: true,
              },
            ],
            viewSpecsOptions: {
              joinValuesWith: " - ",
            },
          },
          {
            title: t("Number of Additional Pairs"),
            fields: [
              {
                name: "Additional Blades",
              },
            ],
            viewSpecsOptions: {
              joinValuesWith: " - ",
            },
          },
        ],
      },
    ].filter(Boolean);
}

function getPlayerStickStickLength(
  userChoices,
  userChoiceValues,
  t = (str) => str
) {
  const stickLength = flow(
    getOr(0, ["Flex", "selectedOption", "addToStickLength"])
  )(userChoices);

  let addToStickLength = 0;

  if (
    userChoices?.addExtension?.value &&
    userChoices?.addExtension?.value === userChoices?.addExtension?.checkedValue
  ) {
    if (userChoices.addExtension?.addToStickLength) {
      addToStickLength = userChoices.addExtension?.addToStickLength;
    } else if (isArray(userChoices.addExtension.checkedLabel)) {
      addToStickLength = flow(
        filter(shouldBeDisplayed(userChoiceValues)),
        head,
        getOr(0, "addToStickLength")
      )(userChoices.addExtension.checkedLabel);
    } else {
      addToStickLength = 0;
    }
  }

  return (data) => {
    return [
      data,
      {
        id: `${data.id}__stick_length`,
        title: t("Stick Length"),
        fields: [
          {
            value:
              stickLength + addToStickLength > 0
                ? `${stickLength + addToStickLength}”`
                : null,
          },
        ],
      },
    ];
  };
}

function getPlayerStickGripTexture(t = (str) => str) {
  return (data) => {
    return [
      data,
      {
        title: t("Grip Texture"),
        fields: [
          {
            name: "Grip Texture",
          },
        ],
      },
    ];
  };
}

function getAdditionalFields(userChoices, userChoiceValues, t) {
  return (data) => {
    if (
      ["Hyperlite2Skate2023__blade", "UltrasonicSkate2021__blade"].includes(
        data.id
      )
    ) {
      return getCustomisedBladeAdditionalFields(userChoices, t)(data);
    }

    if (
      [
        "ProCustomPlayerStick2023__flex",
        "HyperlitePlayerStick2023__flex",
        "NexusGeoPlayerStick2020__flex",
        "SupremePlayerStick2020__flex",
        "AG5NTPlayerStick2023__flex",
        "QuickTurnVaporStick__flex",
        "QuickTurnNexusStick__flex",
        "QuickTurnSupremeStick__flex",
      ].includes(data.id)
    ) {
      return getPlayerStickStickLength(userChoices, userChoiceValues, t)(data);
    }

    if (
      [
        "ProCustomPlayerStick2023__grip",
        "HyperlitePlayerStick2023__grip",
        "NexusGeoPlayerStick2020__grip",
        "AG5NTPlayerStick2023__grip",
        "SupremePlayerStick2020__grip",
      ].includes(data.id)
    ) {
      return getPlayerStickGripTexture(t)(data);
    }

    return [data];
  };
}

function getValuesForField(
  t,
  userChoices,
  userChoiceValues,
  UIVersion,
  isSingleOption
) {
  return ({
    title,
    name,
    hideFromNormalReview,
    fields = [],
    viewSpecsOptions = {},
  }) => {
    const valuesLength = getValuesLength(userChoices)(fields);
    const { joinValuesWith = " | ", displayAs = "row" } = viewSpecsOptions;

    return {
      title,
      name,
      hideFromNormalReview,
      joinValuesWith,
      displayAs,
      values: flow(
        map((field) => {
          // For checkboxes
          if (field.hasOwnProperty("value")) {
            return {
              name: field.name,
              label: null,
              value: field.value,
              image: null,
              isIncluded: null,
            };
          }

          if (field.fields) {
            return getValuesForField(t, userChoices)(field);
          }

          if (!field?.name || !has(userChoices, field.name)) {
            return null;
          }

          const { label, value, image } = getValue(
            userChoices[field.name],
            userChoiceValues
          );

          const newLabel = label && getLabel({ label, userChoiceValues });

          const valueWithReinforced =
            field?.name === "t-trap" &&
            userChoiceValues["t-trap-reinforced"] &&
            `${value} ${t("(Reinforced)")}`;

          const baseLabelValueObject = {
            label:
              UIVersion === 2
                ? (newLabel !== title && value && newLabel) || null
                : (valuesLength > 1 &&
                    newLabel !== title &&
                    value &&
                    newLabel) ||
                  null,
            name: field.name,
            value: valueWithReinforced || value || null,
            image: (!field.ignoreImage && image) || null,
            isIncluded:
              userChoices[field.name]?.selectedOption?.isIncluded || null,
          };

          // For Single product Colors
          if (field.subCategory) {
            // If single product color does not exist (e.g value for outside-zone-color--glove = null), fetch the default color from set-colors
            if (
              isSingleOption &&
              (name === "glove-color-colors" || name === "blocker-color-colors")
            ) {
              // fetching from set-colors
              const { value: setColorValueOverride } = getValue(
                userChoices[field?.name?.split("--")[0]],
                userChoiceValues
              );

              const colorLabelValueObject = {
                ...baseLabelValueObject,
                label:
                  (newLabel !== title &&
                    [value, setColorValueOverride].some(Boolean) &&
                    newLabel) ||
                  null,
                value: value || setColorValueOverride,
              };
              return {
                name: field.name,
                label: field.subCategory,
                value: [colorLabelValueObject] || null,
              };
            }
            return {
              name: field.name,
              label: field.subCategory,
              value: [baseLabelValueObject] || null,
            };
          }

          return baseLabelValueObject;
        }),

        filter((value) => value && (value.values || isNotNil(value.value)))
      )(fields),
    };
  };
}

const reduceWithIndex = reduce.convert({ cap: false });

const getValuesForUIV2 = ({ id, title, name, values }) => {
  if (name === "construction") {
    return {
      id,
      title,
      name,
      values: values.map(({ label, name, value }) => {
        return { label, name, value };
      }),
    };
  }
  return {
    id,
    title,
    name,
    values: flow(
      groupBy("label"),
      mapValues(flatMap(get("value"))),
      reduceWithIndex(
        (acc, value, label) => [
          ...acc,
          {
            label: label === "null" ? null : label,
            value: typeof value[0] === "string" ? value[0] : value,
          },
        ],
        []
      )
    )(values),
  };
};

// * For values that need to be hard coded
const getAdditionalFieldsForUIV2 =
  (userChoices, userChoiceValues, t, isSingleOption) =>
  ({ title, name, values }) => {
    if (name === "glove-specs") {
      return {
        title,
        name,
        values: values.map(({ label, value }) => {
          const foundInternalFit = flow(
            castArray,
            filter(negate(isString)),
            map(get("name")),
            includes("internal-fit")
          )(value);

          if (foundInternalFit) {
            return {
              label,
              value: [
                ...value,
                {
                  label: t("Handedness"),
                  value: userChoices["full-right-hand"]?.selectedOption?.label,
                },
              ],
            };
          }

          return { label, value };
        }),
      };
    }
    if (["glove-color-colors", "blocker-color-colors"].includes(name)) {
      return {
        // Renamed to remove `(Overrides Set Colors)` when a single option
        title: isSingleOption ? title.replace(/ *\([^)]*\) */g, "") : title,
        name,
        values: flow(
          map((x) => {
            return {
              ...x,
              value: x.value.filter((y) => y.value !== null),
            };
          }),
          map((labelValue) => {
            if (labelValue.value.length === 0) {
              return {
                ...labelValue,
                value: [{ label: t("Same as Set Colors"), value: "" }],
              };
            }
            return labelValue;
          })
        )(values),
      };
    }
    return { title, name, values };
  };

const splitAndFilterGoalie =
  (userChoiceValues) =>
  ({ title, fields, name, ...all }) => {
    if (name === "construction") {
      return {
        title,
        name,
        fields: fields.filter(shouldBeDisplayed(userChoiceValues)),
      };
    }
    if (name === "colors") {
      const filteredFieldsByDisplayIf = fields.filter(
        shouldBeDisplayed(userChoiceValues)
      );

      return filteredFieldsByDisplayIf.map(({ reviewTitle, fields, ...a }) => {
        return {
          title: reviewTitle,
          name,
          fields: fields.filter(shouldBeDisplayed(userChoiceValues)),
          ...a,
        };
      });
    }
    return { title, name, fields, ...all };
  };

export default function selectUserChoicesForUi(t) {
  return (state) => {
    const { selectedProductId, volumentalData } = state.app;
    const hasVolumentalData = !isEmpty(volumentalData);
    const userChoices = selectUserChoices(state);
    const userChoiceValues = selectUserChoiceValues(state);
    const UIVersion = state?.app?.formData?.UIVersion;

    const customisations = flow(
      getOr([], ["app", "formData", "products"]),
      find(({ id }) => id === selectedProductId),
      getOr([], ["customisations"])
    )(state);

    if (state.app.formData.id === "goalie-gear") {
      // isSingleOption is used for identifying if only one goalie-gear product is being customised (e.g glove only)
      const isSingleOption = flow(
        pickBy(Boolean),
        keys,
        (arr) => arr.length === 1
      )(selectUserGearEquipmentOptions(state));

      return flow(
        filter(shouldBeDisplayed(userChoiceValues)),
        flatMap(splitAndFilterGoalie(userChoiceValues)),
        map(
          getValuesForField(
            t,
            userChoices,
            userChoiceValues,
            UIVersion,
            isSingleOption
          )
        ),
        filter((field) => !field.hideFromNormalReview),
        map(getValuesForUIV2),
        map(
          getAdditionalFieldsForUIV2(
            userChoices,
            userChoiceValues,
            t,
            isSingleOption
          )
        )
      )(customisations);
    }

    return flow(
      filter(
        ({ hideIfNoVolumentalData }) =>
          hasVolumentalData || !hideIfNoVolumentalData
      ),
      flatMap(getAdditionalFields(userChoices, userChoiceValues, t)),
      map(getValuesForField(t, userChoices, userChoiceValues))
    )(customisations);
  };
}
